'use client';

import React, { useState, useId, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

export interface CollapsibleProps {
  /** Trigger label shown next to the chevron. */
  label: React.ReactNode;
  /** Collapsible body content. */
  children: React.ReactNode;
  /** Initial open state (uncontrolled). */
  defaultOpen?: boolean;
  /** Controlled open state. */
  open?: boolean;
  /** Change callback. */
  onOpenChange?: (open: boolean) => void;
  /** Classes applied to the wrapper. */
  className?: string;
  /** Classes applied to the trigger button. */
  triggerClassName?: string;
  /** Classes applied to the content region. */
  contentClassName?: string;
  /** Icon at the start (before label). Optional. */
  icon?: React.ReactNode;
}

/**
 * Collapsible region with chevron indicator. Used for "About this screen",
 * "Advanced options", "Show legal citation" patterns.
 *
 * Accessible by default — button trigger with aria-expanded, hidden content
 * properly removed from a11y tree when collapsed.
 */
export function Collapsible({
  label,
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  className,
  triggerClassName,
  contentClassName,
  icon,
}: CollapsibleProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const contentId = useId();

  const toggle = useCallback(() => {
    const next = !isOpen;
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  }, [isOpen, isControlled, onOpenChange]);

  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left',
          'text-sm font-medium text-statura-ink transition-colors hover:bg-slate-50',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green/40',
          isOpen && 'border-b border-slate-200',
          triggerClassName,
        )}
      >
        <span className="flex items-center gap-2">
          {icon}
          {label}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      {/* Always render the content region so aria-controls always points to a
          real element (ARIA 1.2 requires the controlled element to exist).
          Visibility is toggled with `hidden` which is semantically equivalent
          to `display:none` and is removed from the accessibility tree. */}
      <div
        id={contentId}
        hidden={!isOpen}
        className={cn('px-4 py-4 text-sm text-slate-700', contentClassName)}
      >
        {children}
      </div>
    </div>
  );
}
